import {useCallback, useRef, useState} from 'react';
import {useIsMount} from '../useIsMount';

export interface UseFunctionWrapResult<TArgs extends unknown[], TResult> {
  fn: (...args: TArgs) => Promise<void>;
  data: TResult | undefined;
  loading: boolean;
  error: Error | undefined;
}

/**
 * Wraps an async function. manages the following:
 * - data state
 * - loading state
 * - error state
 * - refetch function
 */
export function useFunctionWrapV2<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
): UseFunctionWrapResult<TArgs, TResult> {
  // data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [data, setData] = useState<TResult | undefined>(undefined);

  const isMounted = useIsMount();
  const fnRef = useRef(fn);
  fnRef.current = fn;

  // create callback function
  const wrappedFn = useCallback(
    async (...args: TArgs) => {
      setLoading(true);
      setError(undefined);
      try {
        const result = await fnRef.current(...args);
        if (isMounted()) {
          setData(result);
          setLoading(false);
          setError(undefined);
        }
      } catch (err) {
        if (isMounted()) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setData(undefined);
          setLoading(false);
        }
      }
    },
    [isMounted],
  );

  return {
    fn: wrappedFn,
    data,
    loading,
    error,
  };
}
